import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Save,
    User,
    Phone,
    Mail,
    MapPin,
    Loader2
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminCustomerService from "../../services/admin/AdminCustomerService";
import { useNotification } from "../../context/NotificationContext";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

export default function EditCustomerPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { hasPermission } = useAdminPermissions();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        contact_person_name: ""
    });

    useEffect(() => {
        if (!hasPermission("admin")) {
            showNotification({
                type: "failed",
                title: "Access Restricted",
                message: "You do not have permission to edit customer profiles.",
            });
            navigate("/admin/dashboard");
        }
    }, [hasPermission, navigate, showNotification]);

    useEffect(() => {
        const fetchCustomer = async () => {
            try {
                const result = await AdminCustomerService.getDetail(id);
                if (result.status === "success") {
                    const c = result.data;
                    setFormData({
                        name: c.name || "",
                        phone: c.phone || "",
                        email: c.email || "",
                        address: c.address || "",
                        contact_person_name: c.contact_person_name || ""
                    });
                }
            } catch (error) {
                showNotification({
                    type: "failed",
                    title: "Error",
                    message: "Failed to load customer details."
                });
                navigate("/admin/customers");
            } finally {
                setIsLoading(false);
            }
        };
        fetchCustomer();
    }, [id, navigate, showNotification]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const result = await AdminCustomerService.update(id, formData);
            if (result.status === "success") {
                showNotification({
                    type: "success",
                    title: "Success",
                    message: "Customer profile updated successfully."
                });
                navigate(`/admin/customers/${id}`);
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Update Failed",
                message: error.response?.data?.message || "Failed to update customer."
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="animate-spin text-[#62397f]" size={40} />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[800px] mx-auto space-y-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-xl transition-colors"
                    >
                        <ArrowLeft size={20} className="text-gray-500" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                            Edit Customer Profile
                        </h1>
                        <p className="text-sm text-gray-400">Update contact details and preferences</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden shadow-sm">
                    <div className="p-6 lg:p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <User size={14} /> Full Name / Company
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none dark:text-white transition-all"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <User size={14} /> Contact Person Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.contact_person_name}
                                    onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none dark:text-white transition-all"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <Phone size={14} /> Primary Phone
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none dark:text-white transition-all"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <Mail size={14} /> Email Address
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none dark:text-white transition-all"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                <MapPin size={14} /> Physical Address
                            </label>
                            <textarea
                                required
                                rows={3}
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none dark:text-white transition-all resize-none"
                            />
                        </div>
                    </div>

                    <div className="p-6 bg-gray-50 dark:bg-[#212121] border-t border-[#E5E7EB] dark:border-[#2A2A2A] flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-[#262626] transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="flex items-center gap-2 px-8 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-purple-200 dark:shadow-none disabled:opacity-50"
                        >
                            {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                            <span>Save Changes</span>
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
}
